import { priorities, statuses, types } from "@/components/task/utils";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { display_owner } from "@/components/utils/display";
import { DataService } from "@/services/data";
import { ApprenticeshipInterviewStudentType } from "@/types/apprenticeship/enterprise";
import { SFOwnerFieldType } from "@/types/salesforce/fields";
import { TaskType } from "@/types/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSubmit } from "react-router-dom";
import { z } from "zod";

export function CreateOrEditTaskDialog({ task, is_manager, children }: { task?: TaskType; is_manager: boolean; children?: React.ReactNode }) {
    const [open, setOpen] = useState(false);
    const [registrations, setRegistrations] = useState<ApprenticeshipInterviewStudentType[]>([]);

    const submit = useSubmit();
    const [inputValue, setInputValue] = useState<string>("");
    const formSchema = z.object({
        title: z.string(),
        due_date: z.string(),
        description: z.string().optional(),
        status: z.string().optional(),
        priority: z.string().optional(),
        type: z.string().optional(),
        reference_type: z.number().optional(),
        reference_id: z.number().optional(),
        assigned_to: is_manager
            ? z.number({
                  required_error: "L'assignation est requise.",
                  invalid_type_error: "Veuillez sélectionner un CE/CAP"
              })
            : z.number().optional()
    });

    useEffect(() => {
        const controller = new AbortController();

        const fetchRegistrations = async () => {
            if (inputValue.length > 2) {
                try {
                    const _registrations = (await DataService.client
                        .url(`/apprenticeship/registrations/quick_search/?q=${inputValue}&status=CompanyFound,CompanyFulfilled,ContractToBeSigned,ContractSigned,Abandonned,Validated,Terminated`)
                        .signal(controller)
                        .get()
                        .json()) as ApprenticeshipInterviewStudentType[];
                    setRegistrations(_registrations);
                } catch (error: any) {
                    console.error(error);
                }
            }
        };

        fetchRegistrations();
        return () => {
            controller.abort(); // Cancel the previous request if inputValue changes
        };
    }, [inputValue]);

    useEffect(() => {
        if (!open) {
            form.reset();
        }
    }, [open]);

    const form = useForm<z.infer<typeof formSchema>>({
        mode: "onSubmit",
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: task?.title ?? undefined,
            due_date: task?.due_date ?? undefined,
            description: task?.description ?? undefined,
            status: task?.status ?? "backlog",
            priority: task?.priority ?? "low",
            type: task?.type ?? "note",
            reference_type: 88, // apprenticeship.registration to adapt if we add other models
            reference_id: task?.reference?.id ?? undefined,
            assigned_to: task?.assigned_to?.id ?? undefined
        }
    });

    const onSubmit = form.handleSubmit(data => {
        const formattedData = {
            ...data,
            ...(data.assigned_to && { assigned_to: { id: data.assigned_to } })
        };
        submit(formattedData, {
            method: task?.id ? "patch" : "post",
            action: task?.id ? `/${task.id}` : ".",
            encType: "application/json"
        });
        if (setOpen) {
            setOpen(false);
        }
    });

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {children ?? (
                    <Button variant="outline" className="h-full ">
                        Add a task
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{task?.id ? "Edit a task" : "Add a new task"}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={onSubmit} className="grid grid-cols-3 gap-2">
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem className="col-span-3">
                                    <FormLabel>Title</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem className="col-span-3">
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="due_date"
                            render={({ field }) => (
                                <FormItem className="col-span-3">
                                    <FormLabel>Due date</FormLabel>
                                    <FormControl>
                                        <DatePicker
                                            date={field.value ? DateTime.fromISO(field.value) : undefined}
                                            setDate={d => {
                                                field.onChange(d ? d.toISODate() : null);
                                            }}
                                            className="w-full"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="reference_id"
                            render={({ field }) => (
                                <FormItem className="col-span-3">
                                    <FormLabel>Associate to a registration</FormLabel>
                                    <Combobox
                                        name="registration"
                                        value={field.value ? field.value.toString() : undefined}
                                        options={registrations.map(registration => {
                                            return { value: registration.id.toString(), label: `${registration.last_name} ${registration.first_name} (#${registration.id})` };
                                        })}
                                        onValueChange={value => {
                                            field.onChange(value ? parseInt(value) : null);
                                        }}
                                        onInputValueChange={setInputValue}
                                        placeholder="Rechercher un dossier"
                                        className="w-full"
                                        shouldFilter={false}
                                    />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {is_manager && (
                            <FormField
                                control={form.control}
                                name="assigned_to"
                                render={({ field }) => (
                                    <FormItem className="col-span-3">
                                        <FormLabel>Assigner à un CE/CAP</FormLabel>
                                        <Combobox
                                            name="assigned_to"
                                            optionsUrl="/alternance/picklists/agents/"
                                            optionFormatter={(agent: SFOwnerFieldType) => {
                                                return { value: agent.crm_user_id?.toString() || "0", label: display_owner(agent, true) };
                                            }}
                                            className="w-full"
                                            onValueChange={value => {
                                                field.onChange(value ? parseInt(value) : null);
                                            }}
                                        />
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem className="col-span-1">
                                    <FormLabel>Statut</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Statut" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {statuses.map(status => (
                                                <SelectItem key={status.value} value={status.value}>
                                                    <div className="flex flex-row items-center space-x-1">
                                                        <div>{status.icon}</div>
                                                        <div>{status.label}</div>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="priority"
                            render={({ field }) => (
                                <FormItem className="col-span-1">
                                    <FormLabel>Priorité</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Priorité" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {priorities.map(priority => (
                                                <SelectItem key={priority.value} value={priority.value}>
                                                    <div className="flex flex-row items-center space-x-1">
                                                        <div>{priority.icon}</div>
                                                        <div>{priority.label}</div>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="type"
                            render={({ field }) => (
                                <FormItem className="col-span-1">
                                    <FormLabel>Type</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Type" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {types.map(type => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    <div className="flex flex-row items-center space-x-1">
                                                        <div>{type.icon}</div>
                                                        <div>{type.label}</div>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="col-span-3 mt-4 flex justify-center">
                            <Button
                                type="reset"
                                variant={"secondary"}
                                className="mr-2"
                                onClick={() => {
                                    setOpen(false);
                                }}
                            >
                                Annuler
                            </Button>
                            <Button type="submit">{task?.id ? "Modifier" : "Enregistrer"}</Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
